import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { AlertTriangle, AlertOctagon, X } from 'lucide-react'

// ANU-91 — entry point for triggering BLIND MAN ceasefire.
// Rendered in:
//   • RefereeView BLIND MAN tab (every IGA gets one)
//   • Command Center header (GM-only, live events only)
//
// Two-step confirmation by design — the button itself never fires the
// RPC. The confirm dialog with optional reason field is the actual
// trigger. Errors surface as a visible toast inside the dialog; we do
// NOT swallow them silently per the safety-critical spec.
export default function CallBlindManButton({ eventId, compact = false }) {
  const [showConfirm, setShowConfirm] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const triggerBlindMan = async () => {
    setSubmitting(true)
    setError('')
    const { error: err } = await supabase.rpc('call_blind_man', {
      p_event_id: eventId,
      p_reason: reason.trim() || null,
    })
    if (err) {
      setError(err.message || 'Failed to call BLIND MAN. Try again or signal verbally.')
      setSubmitting(false)
      return
    }
    setSubmitting(false)
    setShowConfirm(false)
    setReason('')
  }

  const buttonStyle = compact
    ? {
        background: 'rgba(255,42,42,.12)',
        border: '1px solid rgba(255,42,42,.5)',
        color: '#ff4040',
        padding: '6px 12px',
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }
    : {
        width: '100%',
        minHeight: 64,
        padding: '16px 18px',
        background: 'rgba(255,42,42,.1)',
        border: '1px solid rgba(255,42,42,.55)',
        color: '#ff4040',
        fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
        fontWeight: 800,
        fontSize: 18,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
      }

  return (
    <>
      <button type="button" onClick={() => setShowConfirm(true)} style={buttonStyle}>
        <AlertOctagon size={compact ? 14 : 22} /> BLIND MAN
      </button>

      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.95)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{
            background: '#1a0a0a',
            border: '2px solid #ff4040',
            width: '100%',
            maxWidth: 460,
            padding: 22,
            color: '#fff',
            fontFamily: "'Inter', system-ui, sans-serif",
            boxShadow: '0 0 40px rgba(255,42,42,.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 800,
                color: '#ff4040',
                fontSize: 22,
                margin: 0,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <AlertTriangle size={20} /> CONFIRM CEASEFIRE
              </h2>
              <button onClick={() => !submitting && setShowConfirm(false)}
                disabled={submitting}
                style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', minHeight: 44, minWidth: 44 }}>
                <X size={20} />
              </button>
            </div>

            <p style={{ color: '#ffd0d0', fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
              This calls a real-world ceasefire. Every player on this event sees a full-screen overlay until the GM lifts the hold. Use for injury, safety hazard, or a hard stop. Do not use this for normal pauses.
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ color: '#aaa', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6, display: 'block', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}>
                Reason <span style={{ color: '#666' }}>(optional, broadcast to all players)</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason: injury, safety hazard…"
                maxLength={200}
                style={{
                  width: '100%',
                  background: 'rgba(0,0,0,.4)',
                  color: '#fff',
                  border: '1px solid rgba(255,42,42,.4)',
                  padding: '10px 12px',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>

            {error && (
              <div style={{ background: 'rgba(255,200,0,.12)', border: '1px solid rgba(255,200,0,.5)', padding: 10, color: '#ffd060', fontSize: 12, marginBottom: 12 }}>
                {error} — if this keeps failing, signal verbally and find a GM.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setShowConfirm(false)} disabled={submitting}
                style={{
                  flex: 1, minHeight: 52, background: 'transparent', border: '1px solid #555',
                  color: '#aaa', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14,
                  letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer',
                }}>
                CANCEL
              </button>
              <button type="button" onClick={triggerBlindMan} disabled={submitting}
                style={{
                  flex: 2, minHeight: 52, background: '#cc0000', border: 'none',
                  color: '#fff', fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 15,
                  letterSpacing: '0.16em', textTransform: 'uppercase', cursor: submitting ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: submitting ? 0.7 : 1,
                }}>
                <AlertOctagon size={16} />
                {submitting ? 'CALLING...' : 'CONFIRM BLIND MAN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
